import { useDashboardStore } from '@/state/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { FloorView } from '@/api/rest';

/**
 * Compact building thumbnail floating in the corner of the pod-room
 * view.  Acts as a navigation aid: each pod is a clickable dot,
 * floors are stacked top-to-bottom (canary -> active -> reduced ->
 * frozen).  The currently-selected pod glows; clicking any other pod
 * jumps to that pod's room without going through the building view.
 *
 * Phase 10: tiny pure-DOM/SVG widget so it doesn't fight the Pixi
 * canvas for the GL context.  Touch-friendly on mobile (each pod dot
 * is at least 12 px and the whole minimap collapses to a smaller size
 * to stay out of the way).
 */

const FLOOR_COLOR: Record<string, string> = {
  canary_lab: '#1f3a5f',
  active_offices: '#1f6feb22',
  reduced_floor: '#d2992233',
  frozen_basement: '#58a6ff44',
};

const RISK_COLOR: Record<string, string> = {
  active: '#2ea043',
  watch: '#d29922',
  reduced: '#db6d28',
  frozen: '#58a6ff',
};

interface MinimapProps {
  selectedPodId: string;
}

export function BuildingMinimap({ selectedPodId }: MinimapProps) {
  const building = useDashboardStore((s) => s.building);
  const pods = useDashboardStore((s) => s.pods);
  const selectPod = useDashboardStore((s) => s.selectPod);
  const goToBuilding = useDashboardStore((s) => s.goToBuilding);
  const bp = useBreakpoint();

  if (!building) return null;
  const isMobile = bp === 'mobile';
  // Compact on mobile, full on tablet/desktop.
  const width = isMobile ? 120 : 160;
  const floorH = isMobile ? 14 : 18;
  const dotSize = isMobile ? 10 : 12;

  return (
    <aside
      aria-label="Building minimap"
      style={{
        position: 'absolute',
        right: isMobile ? 8 : 12,
        bottom: isMobile ? 60 : 60,
        width,
        background: '#0d1117ee',
        border: '1px solid #30363d',
        borderRadius: 8,
        padding: isMobile ? 6 : 8,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
        zIndex: 3,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <strong
          style={{
            fontSize: 9,
            color: '#8b949e',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          Minimap
        </strong>
        <button
          type="button"
          onClick={goToBuilding}
          aria-label="Back to building view"
          title="Zoom out to building"
          style={{
            background: 'transparent',
            color: '#8b949e',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: 11,
            textDecoration: 'underline',
          }}
        >
          all floors
        </button>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {building.floors.map((floor) => (
          <FloorRow
            key={floor.id}
            floor={floor}
            floorH={floorH}
            dotSize={dotSize}
            selectedPodId={selectedPodId}
            getRiskState={(podId) =>
              pods[podId]?.riskState ??
              floor.pods.find((p) => p.pod_id === podId)?.risk_state ??
              'active'
            }
            onSelect={(podId) => {
              if (podId !== selectedPodId) selectPod(podId);
            }}
          />
        ))}
      </div>
    </aside>
  );
}

function FloorRow({
  floor,
  floorH,
  dotSize,
  selectedPodId,
  getRiskState,
  onSelect,
}: {
  floor: FloorView;
  floorH: number;
  dotSize: number;
  selectedPodId: string;
  getRiskState: (podId: string) => string;
  onSelect: (podId: string) => void;
}) {
  return (
    <div
      style={{
        background: FLOOR_COLOR[floor.id] ?? '#161b22',
        borderRadius: 4,
        height: floorH,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '0 6px',
        overflow: 'hidden',
      }}
      title={floor.label}
    >
      {floor.pods.length === 0 ? (
        <span style={{ fontSize: 8, color: '#6e7681', fontStyle: 'italic' }}>
          empty
        </span>
      ) : (
        floor.pods.map((pod) => {
          const isSelected = pod.pod_id === selectedPodId;
          const accent = RISK_COLOR[getRiskState(pod.pod_id)] ?? '#8b949e';
          return (
            <button
              key={pod.pod_id}
              type="button"
              onClick={() => onSelect(pod.pod_id)}
              aria-label={`${pod.name ?? pod.pod_id} -- ${getRiskState(pod.pod_id)}`}
              title={pod.name ?? pod.pod_id}
              style={{
                width: dotSize,
                height: dotSize,
                borderRadius: '50%',
                background: isSelected ? accent : `${accent}55`,
                border: isSelected ? `2px solid ${accent}` : `1px solid ${accent}88`,
                padding: 0,
                cursor: 'pointer',
                flexShrink: 0,
                boxShadow: isSelected ? `0 0 6px ${accent}` : 'none',
                transition: 'transform .12s ease',
                touchAction: 'manipulation',
              }}
            />
          );
        })
      )}
    </div>
  );
}
